/**
 * Admin Subscription Login Page
 * Dedicated login page for subscription management and admin access
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Building2, Shield, ArrowRight, Sparkles, Home, CheckCircle, UserPlus, LogIn, Briefcase } from 'lucide-react';
import { getSubscription } from '../services/SubscriptionService';
import { hasActiveSubscription } from '../utils/planLimits';
import fitzaaLogo from '../assets/appIcon.png';
import { initiateBusinessSignUp } from '../services/WebCloudFunctionsService';

const AdminSubscriptionLoginPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { login, loginWithGoogle, signUp, isLoading, isAuthenticated, user } = useAuth();
    const { theme } = useTheme();

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [displayName, setDisplayName] = useState('');
    const [isSignUp, setIsSignUp] = useState(false);
    const [isBusinessSignUp, setIsBusinessSignUp] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [authLoading, setAuthLoading] = useState<'google' | null>(null);
    const [showEmailForm, setShowEmailForm] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const errorRef = useRef<string>('');

    // Restore error from sessionStorage on mount (in case component remounted)
    useEffect(() => {
        const savedError = sessionStorage.getItem('loginError');
        if (savedError && !errorMessage) {
            errorRef.current = savedError;
            setErrorMessage(savedError);
            setShowEmailForm(true);
        }
    }, []);

    // Get return URL from query params, location state, or default to subscription management
    const returnUrl = new URLSearchParams(location.search).get('returnUrl') ||
        (location.state as any)?.from?.pathname ||
        '/subscription';


    // Only redirect if authenticated, not loading, no error, and form is not shown
    useEffect(() => {
        const checkSubscriptionAndRedirect = async () => {
            const isLoginPage = location.pathname === '/admin/login' ||
                location.pathname === '/subscription/login' ||
                location.pathname === '/login';

            if (!isLoginPage) return;
            if (isLoading) return;
            if (showEmailForm || errorMessage) {
                return;
            }
            if (isSubmitting) {
                return;
            }

            console.log('[AdminSubscriptionLoginPage] Checking redirect - authenticated:', isAuthenticated, 'user:', !!user);
            if (isAuthenticated && user) {
                const orgId = user.id;
                if (orgId) {
                    try {
                        const subscription = await getSubscription(orgId);
                        if (user.organizationId && user.organizationId !== 'individual' && user.organizationId !== user.id) {
                            const orgSubscription = await getSubscription(user.organizationId);
                            if (orgSubscription && hasActiveSubscription(orgSubscription)) {
                                navigate(returnUrl);
                                return;
                            }
                        }
                        if (!subscription || subscription.status === 'expired' || subscription.status === 'cancelled') {
                            navigate('/onboarding');
                            return;
                        }
                        navigate(returnUrl);
                    } catch (error) {
                        console.error('[AdminSubscriptionLoginPage] Error checking subscription:', error);
                        navigate('/onboarding');
                    }
                } else {
                    navigate('/onboarding');
                }
            }
        };
        checkSubscriptionAndRedirect();
    }, [isAuthenticated, isLoading, user, navigate, returnUrl, location.pathname, showEmailForm, errorMessage, isSubmitting]);


    const handleEmailSignIn = async (e: React.FormEvent) => {
        e.preventDefault();

        errorRef.current = '';
        sessionStorage.removeItem('loginError');
        setErrorMessage('');
        setIsSubmitting(true);
        setShowEmailForm(true);

        // Client-side validation for sign-up
        if (isSignUp) {
            if (!displayName || displayName.trim().length === 0) {
                setErrorMessage('Display name is required.');
                setIsSubmitting(false);
                return;
            }
            if (!email || email.trim().length === 0) {
                setErrorMessage('Email is required.');
                setIsSubmitting(false);
                return;
            }
            if (!password || password.length < 6) {
                setErrorMessage('Password must be at least 6 characters long.');
                setIsSubmitting(false);
                return;
            }
        }

        try {
            if (isSignUp) {
                await signUp(email, password, displayName);
            } else {
                await login(email, password);
            }
        } catch (error: any) {
            console.error('CATCH EXECUTED', error);
            // Log full error details for debugging
            console.error('=== SIGN-UP ERROR DETAILS ===');
            console.error('Error code:', error?.code);
            console.error('Error message:', error?.message);
            console.error('Full error object:', error);
            console.error('Custom data:', error?.customData);
            console.error('Server response:', error?.customData?.serverResponse);
            console.error('Error toString:', error?.toString());

            // Try to extract Firebase error details
            try {
                const errorString = JSON.stringify(error, Object.getOwnPropertyNames(error));
                console.error('Error JSON:', errorString);
            } catch (e) {
                console.error('Could not stringify error:', e);
            }

            const errorCode = error?.code || '';
            const customDataMessage = error?.customData?.message || '';
            const serverResponse = error?.customData?.serverResponse || {};

            // Extract actual error code from customData.message if it exists
            // Firebase sometimes wraps the real error in customData.message
            let actualErrorCode = errorCode;
            if (customDataMessage) {
                const emailAlreadyInUseMatch = customDataMessage.match(/auth\/email-already-in-use/);
                const weakPasswordMatch = customDataMessage.match(/auth\/weak-password/);
                const invalidEmailMatch = customDataMessage.match(/auth\/invalid-email/);

                if (emailAlreadyInUseMatch) {
                    actualErrorCode = 'auth/email-already-in-use';
                } else if (weakPasswordMatch) {
                    actualErrorCode = 'auth/weak-password';
                } else if (invalidEmailMatch) {
                    actualErrorCode = 'auth/invalid-email';
                }
            }

            let displayMessage = 'Authentication failed. Please try again.';

            if (!isSignUp) {
                // Sign-in error handling
                if (actualErrorCode === 'auth/user-not-found' || actualErrorCode === 'auth/invalid-credential' ||
                    customDataMessage.includes('auth/invalid-credential') || customDataMessage.includes('invalid-credential')) {
                    displayMessage = 'No account found with this email address.';
                } else if (actualErrorCode === 'auth/wrong-password') {
                    displayMessage = 'Incorrect password. Please try again.';
                } else if (actualErrorCode === 'auth/network-request-failed') {
                    if (customDataMessage.includes('invalid-credential') || customDataMessage.includes('auth/invalid-credential')) {
                        displayMessage = 'No account found with this email address.';
                    } else if (customDataMessage.includes('too-many-requests') || customDataMessage.includes('auth/too-many-requests')) {
                        displayMessage = 'Too many login attempts. Please try again later.';
                    } else {
                        displayMessage = 'Network error. Please try again.';
                    }
                } else if (actualErrorCode === 'auth/too-many-requests') {
                    displayMessage = 'Too many login attempts. Please try again later.';
                }
            } else {
                // Sign-up error handling - use actualErrorCode which extracts from customData.message
                if (actualErrorCode === 'auth/email-already-in-use') {
                    displayMessage = 'This email is already registered. Please sign in instead.';
                } else if (actualErrorCode === 'auth/weak-password') {
                    displayMessage = 'Password is too weak. Please use at least 6 characters.';
                } else if (actualErrorCode === 'auth/invalid-email') {
                    displayMessage = 'Invalid email address.';
                } else if (errorCode === 'auth/network-request-failed' || actualErrorCode === 'auth/network-request-failed') {
                    // Network errors during sign-up might be validation errors
                    // We already extracted the actual error code above, but check one more time
                    if (customDataMessage.includes('email-already-in-use')) {
                        displayMessage = 'This email is already registered. Please sign in instead.';
                    } else if (customDataMessage.includes('weak-password')) {
                        displayMessage = 'Password is too weak. Please use at least 6 characters.';
                    } else if (customDataMessage.includes('invalid-email')) {
                        displayMessage = 'Invalid email address.';
                    } else {
                        displayMessage = 'Network error during sign-up. Please check your connection and try again.';
                    }
                }
            }

            // Store in both ref and sessionStorage (persists across remounts)
            errorRef.current = displayMessage;
            sessionStorage.setItem('loginError', displayMessage);
            setErrorMessage(displayMessage);
            setShowEmailForm(true);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleGoogleSignIn = async () => {
        setAuthLoading('google');
        setErrorMessage('');
        try {
            await loginWithGoogle();
        } catch (error: any) {
            setErrorMessage(error.message || 'Google sign-in failed');
        } finally {
            setAuthLoading(null);
        }
    };

    const handleBusinessSignUp = async (e: React.FormEvent) => {
        e.preventDefault();

        errorRef.current = '';
        sessionStorage.removeItem('loginError');
        setErrorMessage('');
        setIsSubmitting(true);

        // Client-side validation
        if (!displayName || displayName.trim().length === 0) {
            setErrorMessage('Display name is required.');
            setIsSubmitting(false);
            return;
        }
        if (!email || email.trim().length === 0) {
            setErrorMessage('Email is required.');
            setIsSubmitting(false);
            return;
        }

        try {
            await initiateBusinessSignUp(email, displayName);
            // Success - redirect to pending page or show success message
            navigate('/verify-business-signup-pending', {
                state: { email, displayName },
                replace: true
            });
        } catch (error: any) {
            console.error('Business sign-up error:', error);
            let displayMessage = 'Failed to send verification email. Please try again.';

            if (error?.code === 'invalid-argument' || error?.message) {
                displayMessage = error.message || displayMessage;
            }

            errorRef.current = displayMessage;
            setErrorMessage(displayMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleBackToMain = () => {
        // Don't allow going back while submitting
        if (isSubmitting) {
            return;
        }
        // Clear all error state
        errorRef.current = '';
        sessionStorage.removeItem('loginError');
        setErrorMessage('');
        setShowEmailForm(false);
        setIsBusinessSignUp(false);
        setIsSignUp(false);
    };

    if (isLoading) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-purple-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center p-4 py-8">
            <div className="w-full max-w-6xl">
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl overflow-hidden">
                    {/* Header */}
                    <div className="bg-gradient-to-r from-purple-600 to-blue-600 p-6 text-center">
                        <div className="flex items-center justify-center space-x-3 mb-3">
                            <img src={fitzaaLogo} alt="Fitzaa" className="w-10 h-10" />
                            <h1 className="text-2xl font-bold text-white">Admin Portal</h1>
                        </div>
                        <p className="text-purple-100 text-sm">Subscription Management</p>
                    </div>

                    {/* Main Content */}
                    <div className="p-8">
                        {!showEmailForm && !errorMessage ? (
                            <>
                                {/* Two Column Layout: New User & Returning User */}
                                <div className="grid md:grid-cols-2 gap-6 mb-8">
                                    {/* New User Registration Section */}
                                    <div className="border-2 border-purple-200 dark:border-purple-800 rounded-xl p-6 bg-gradient-to-br from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20">
                                        <div className="flex items-center space-x-3 mb-4">
                                            <div className="p-2 bg-purple-100 dark:bg-purple-900/50 rounded-lg">
                                                <UserPlus className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                                            </div>
                                            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                                                New User Registration
                                            </h2>
                                        </div>
                                        <p className="text-sm text-gray-600 dark:text-gray-300 mb-6">
                                            Create an account to start a subscription and set up your organization.
                                        </p>

                                        <div className="space-y-3">
                                            <button
                                                onClick={() => {
                                                    setIsSignUp(true);
                                                    setIsBusinessSignUp(false);
                                                    setShowEmailForm(true);
                                                }}
                                                className="w-full px-6 py-3.5 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl font-semibold transition-all hover:from-purple-700 hover:to-blue-700 hover:shadow-lg flex items-center justify-center space-x-2"
                                            >
                                                <span>Sign Up with Email</span>
                                                <ArrowRight className="w-5 h-5" />
                                            </button>

                                            <button
                                                onClick={() => {
                                                    setIsBusinessSignUp(true);
                                                    setIsSignUp(false);
                                                    setShowEmailForm(true);
                                                }}
                                                className="w-full px-6 py-3.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-semibold transition-all hover:from-indigo-700 hover:to-purple-700 hover:shadow-lg flex items-center justify-center space-x-2"
                                            >
                                                <Briefcase className="w-5 h-5" />
                                                <span>Sign Up for Business Account</span>
                                                <ArrowRight className="w-5 h-5" />
                                            </button>

                                            <button
                                                onClick={async () => {
                                                    setIsSignUp(true);
                                                    await handleGoogleSignIn();
                                                }}
                                                disabled={isLoading || authLoading !== null}
                                                className="w-full flex items-center justify-center space-x-3 px-6 py-3.5 bg-white dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 rounded-xl font-semibold text-gray-900 dark:text-white transition-all hover:border-purple-400 dark:hover:border-purple-500 hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                {authLoading === 'google' ? (
                                                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-purple-600"></div>
                                                ) : (
                                                    <>
                                                        <svg className="w-5 h-5" viewBox="0 0 24 24">
                                                            <path
                                                                fill="currentColor"
                                                                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                                                            />
                                                            <path
                                                                fill="currentColor"
                                                                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                                                            />
                                                            <path
                                                                fill="currentColor"
                                                                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                                                            />
                                                            <path
                                                                fill="currentColor"
                                                                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                                                            />
                                                        </svg>
                                                        <span>Sign Up with Google</span>
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    </div>

                                    {/* Returning User Section */}
                                    <div className="border-2 border-blue-200 dark:border-blue-800 rounded-xl p-6 bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20">
                                        <div className="flex items-center space-x-3 mb-4">
                                            <div className="p-2 bg-blue-100 dark:bg-blue-900/50 rounded-lg">
                                                <LogIn className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                                            </div>
                                            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                                                Returning User
                                            </h2>
                                        </div>
                                        <p className="text-sm text-gray-600 dark:text-gray-300 mb-6">
                                            Log in to access your subscription dashboard and manage billing.
                                        </p>

                                        <div className="space-y-3">
                                            <button
                                                onClick={() => {
                                                    setIsSignUp(false);
                                                    setShowEmailForm(true);
                                                }}
                                                className="w-full px-6 py-3.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold transition-all hover:from-blue-700 hover:to-purple-700 hover:shadow-lg flex items-center justify-center space-x-2"
                                            >
                                                <span>Sign In with Email</span>
                                                <ArrowRight className="w-5 h-5" />
                                            </button>

                                            <button
                                                onClick={async () => {
                                                    setIsSignUp(false);
                                                    await handleGoogleSignIn();
                                                }}
                                                disabled={isLoading || authLoading !== null}
                                                className="w-full flex items-center justify-center space-x-3 px-6 py-3.5 bg-white dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 rounded-xl font-semibold text-gray-900 dark:text-white transition-all hover:border-blue-400 dark:hover:border-blue-500 hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                {authLoading === 'google' ? (
                                                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                                                ) : (
                                                    <>
                                                        <svg className="w-5 h-5" viewBox="0 0 24 24">
                                                            <path
                                                                fill="currentColor"
                                                                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                                                            />
                                                            <path
                                                                fill="currentColor"
                                                                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                                                            />
                                                            <path
                                                                fill="currentColor"
                                                                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                                                            />
                                                            <path
                                                                fill="currentColor"
                                                                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                                                            />
                                                        </svg>
                                                        <span>Sign In with Google</span>
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* Subscription Plans Section */}
                                <div className="border-t border-gray-200 dark:border-gray-700 pt-8">
                                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 text-center">
                                        Subscription Plans
                                    </h3>
                                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                                        {/* Individual Plan */}
                                        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-800/50">
                                            <div className="flex items-start justify-between mb-2">
                                                <h4 className="font-semibold text-sm text-gray-900 dark:text-white">Individual</h4>
                                                <span className="text-xs font-bold text-purple-600 dark:text-purple-400">$5/mo</span>
                                            </div>
                                            <ul className="space-y-1 text-xs text-gray-600 dark:text-gray-300">
                                                <li className="flex items-start">
                                                    <CheckCircle className="w-3 h-3 text-purple-600 dark:text-purple-400 mr-1.5 mt-0.5 flex-shrink-0" />
                                                    <span>Monthly payment, no admin portal</span>
                                                </li>
                                                <li className="flex items-start">
                                                    <CheckCircle className="w-3 h-3 text-purple-600 dark:text-purple-400 mr-1.5 mt-0.5 flex-shrink-0" />
                                                    <span>Use existing challenges & leaderboards</span>
                                                </li>
                                                <li className="flex items-start">
                                                    <CheckCircle className="w-3 h-3 text-purple-600 dark:text-purple-400 mr-1.5 mt-0.5 flex-shrink-0" />
                                                    <span>Up to 3 family members (free, auto-expire)</span>
                                                </li>
                                            </ul>
                                        </div>

                                        {/* Starter Plan */}
                                        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-800/50">
                                            <div className="flex items-start justify-between mb-2">
                                                <h4 className="font-semibold text-sm text-gray-900 dark:text-white">Starter</h4>
                                                <span className="text-xs font-bold text-purple-600 dark:text-purple-400">$5/user/mo</span>
                                            </div>
                                            <ul className="space-y-1 text-xs text-gray-600 dark:text-gray-300">
                                                <li className="flex items-start">
                                                    <CheckCircle className="w-3 h-3 text-purple-600 dark:text-purple-400 mr-1.5 mt-0.5 flex-shrink-0" />
                                                    <span>Up to 100 employees</span>
                                                </li>
                                                <li className="flex items-start">
                                                    <CheckCircle className="w-3 h-3 text-purple-600 dark:text-purple-400 mr-1.5 mt-0.5 flex-shrink-0" />
                                                    <span>Basic & team challenges (editable dates)</span>
                                                </li>
                                                <li className="flex items-start">
                                                    <CheckCircle className="w-3 h-3 text-purple-600 dark:text-purple-400 mr-1.5 mt-0.5 flex-shrink-0" />
                                                    <span>Can re-open expired challenges</span>
                                                </li>
                                                <li className="flex items-start">
                                                    <CheckCircle className="w-3 h-3 text-purple-600 dark:text-purple-400 mr-1.5 mt-0.5 flex-shrink-0" />
                                                    <span>Activity tracking, leaderboards, email support</span>
                                                </li>
                                            </ul>
                                        </div>

                                        {/* Professional Plan */}
                                        <div className="border-2 border-purple-300 dark:border-purple-600 rounded-lg p-4 bg-purple-50 dark:bg-purple-900/20">
                                            <div className="flex items-start justify-between mb-2">
                                                <h4 className="font-semibold text-sm text-gray-900 dark:text-white">Professional</h4>
                                                <span className="text-xs font-bold text-purple-600 dark:text-purple-400">$8/user/mo</span>
                                            </div>
                                            <ul className="space-y-1 text-xs text-gray-600 dark:text-gray-300">
                                                <li className="flex items-start">
                                                    <CheckCircle className="w-3 h-3 text-purple-600 dark:text-purple-400 mr-1.5 mt-0.5 flex-shrink-0" />
                                                    <span>Up to 500 employees</span>
                                                </li>
                                                <li className="flex items-start">
                                                    <CheckCircle className="w-3 h-3 text-purple-600 dark:text-purple-400 mr-1.5 mt-0.5 flex-shrink-0" />
                                                    <span>10 custom challenges (create/reuse templates)</span>
                                                </li>
                                                <li className="flex items-start">
                                                    <CheckCircle className="w-3 h-3 text-purple-600 dark:text-purple-400 mr-1.5 mt-0.5 flex-shrink-0" />
                                                    <span>Analytics dashboard</span>
                                                </li>
                                                <li className="flex items-start">
                                                    <CheckCircle className="w-3 h-3 text-purple-600 dark:text-purple-400 mr-1.5 mt-0.5 flex-shrink-0" />
                                                    <span>Rewards & incentives for leaderboard winners</span>
                                                </li>
                                                <li className="flex items-start">
                                                    <CheckCircle className="w-3 h-3 text-purple-600 dark:text-purple-400 mr-1.5 mt-0.5 flex-shrink-0" />
                                                    <span>Priority support</span>
                                                </li>
                                            </ul>
                                        </div>

                                        {/* Enterprise Plan */}
                                        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-800/50">
                                            <div className="flex items-start justify-between mb-2">
                                                <h4 className="font-semibold text-sm text-gray-900 dark:text-white">Enterprise</h4>
                                                <span className="text-xs font-bold text-purple-600 dark:text-purple-400">Custom</span>
                                            </div>
                                            <ul className="space-y-1 text-xs text-gray-600 dark:text-gray-300">
                                                <li className="flex items-start">
                                                    <CheckCircle className="w-3 h-3 text-purple-600 dark:text-purple-400 mr-1.5 mt-0.5 flex-shrink-0" />
                                                    <span>Unlimited employees</span>
                                                </li>
                                                <li className="flex items-start">
                                                    <CheckCircle className="w-3 h-3 text-purple-600 dark:text-purple-400 mr-1.5 mt-0.5 flex-shrink-0" />
                                                    <span>All features included</span>
                                                </li>
                                                <li className="flex items-start">
                                                    <CheckCircle className="w-3 h-3 text-purple-600 dark:text-purple-400 mr-1.5 mt-0.5 flex-shrink-0" />
                                                    <span>White-label, dedicated account manager</span>
                                                </li>
                                                <li className="flex items-start">
                                                    <CheckCircle className="w-3 h-3 text-purple-600 dark:text-purple-400 mr-1.5 mt-0.5 flex-shrink-0" />
                                                    <span>Custom integrations & advanced analytics</span>
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <>
                                {/* Email/Password Form */}
                                <div className="max-w-md mx-auto">
                                    <div className="space-y-6">
                                        <div>
                                            <button
                                                onClick={handleBackToMain}
                                                className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-4 flex items-center space-x-2 text-sm"
                                            >
                                                <ArrowRight className="w-4 h-4 rotate-180" />
                                                <span>Back</span>
                                            </button>
                                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                                                {isBusinessSignUp ? 'Business Account Sign-Up' : isSignUp ? 'Create Account' : 'Sign In'}
                                            </h2>
                                            <p className="text-gray-600 dark:text-gray-400 text-sm">
                                                {isBusinessSignUp
                                                    ? 'Enter your corporate email to receive a verification link'
                                                    : isSignUp
                                                        ? 'Enter your details to create an account'
                                                        : 'Enter your email and password to continue'}
                                            </p>
                                        </div>

                                        {/* Error Message */}
                                        {(errorRef.current || sessionStorage.getItem('loginError')) && (
                                            <div className={`p-4 rounded-xl text-sm mb-4 ${(errorRef.current || sessionStorage.getItem('loginError') || '').includes('No account found')
                                                ? 'bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-300 dark:border-blue-700 text-blue-800 dark:text-blue-200'
                                                : 'bg-red-50 dark:bg-red-900/20 border-2 border-red-300 dark:border-red-700 text-red-800 dark:text-red-200'
                                                }`}>
                                                <div className="flex items-start space-x-3">
                                                    <div className="flex-1">
                                                        <p className="font-medium mb-2 text-base">{errorRef.current || sessionStorage.getItem('loginError') || ''}</p>
                                                        {(errorRef.current || sessionStorage.getItem('loginError') || '').includes('No account found') && (
                                                            <div className="mt-3">
                                                                <p className="text-sm mb-3 text-blue-700 dark:text-blue-300">
                                                                    Would you like to create a new account?
                                                                </p>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => {
                                                                        setIsSignUp(true);
                                                                        setErrorMessage('');
                                                                        setPassword('');
                                                                    }}
                                                                    className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
                                                                >
                                                                    Create Account
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        <form onSubmit={isBusinessSignUp ? handleBusinessSignUp : handleEmailSignIn} className="space-y-4">
                                            {(isSignUp || isBusinessSignUp) && (
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                        Display Name
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={displayName}
                                                        onChange={(e) => setDisplayName(e.target.value)}
                                                        required={isSignUp || isBusinessSignUp}
                                                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                                        placeholder="Your name"
                                                    />
                                                    {isBusinessSignUp && (
                                                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                                            Please use your corporate email address (personal email providers like Gmail are not allowed)
                                                        </p>
                                                    )}
                                                </div>
                                            )}

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                    Email
                                                </label>
                                                <input
                                                    type="email"
                                                    value={email}
                                                    onChange={(e) => setEmail(e.target.value)}
                                                    required
                                                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                                    placeholder={isBusinessSignUp ? "your.name@company.com" : "you@example.com"}
                                                />
                                            </div>

                                            {!isBusinessSignUp && (
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                        Password
                                                    </label>
                                                    <input
                                                        type="password"
                                                        value={password}
                                                        onChange={(e) => setPassword(e.target.value)}
                                                        required
                                                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                                        placeholder="••••••••"
                                                    />
                                                </div>
                                            )}

                                            <button
                                                type="submit"
                                                disabled={isLoading || isSubmitting}
                                                className="w-full px-6 py-3.5 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl font-semibold transition-all hover:from-purple-700 hover:to-blue-700 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                                            >
                                                {(isLoading || isSubmitting) ? (
                                                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                                                ) : (
                                                    <>
                                                        <span>
                                                            {isBusinessSignUp
                                                                ? 'Send Verification Email'
                                                                : isSignUp
                                                                    ? 'Create Account'
                                                                    : 'Sign In'}
                                                        </span>
                                                        <ArrowRight className="w-5 h-5" />
                                                    </>
                                                )}
                                            </button>
                                        </form>

                                        {!isBusinessSignUp && !isSignUp && (
                                            <div className="text-center">
                                                <button
                                                    type="button"
                                                    onClick={() => navigate('/forgot-password')}
                                                    className="text-sm text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 font-medium"
                                                >
                                                    Forgot your password?
                                                </button>
                                            </div>
                                        )}
                                        {!isBusinessSignUp && (
                                            <div className="text-center">
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        // Clear error state when switching modes
                                                        errorRef.current = '';
                                                        sessionStorage.removeItem('loginError');
                                                        setErrorMessage('');
                                                        setPassword('');
                                                        setIsSignUp(!isSignUp);
                                                    }}
                                                    className="text-sm text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 font-medium"
                                                >
                                                    {isSignUp
                                                        ? 'Already have an account? Sign in'
                                                        : "Don't have an account? Sign up"}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </>
                        )}

                        {/* Footer */}
                        <div className="px-8 pb-6 space-y-3">
                            <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                                <div className="text-center mb-3">
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                                        Using the Mobile App?
                                    </p>
                                    <button
                                        onClick={() => navigate('/app/login')}
                                        className="text-sm text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 font-medium"
                                    >
                                        Go to App Login →
                                    </button>
                                </div>
                            </div>

                            <div className="text-center">
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                    Need help?{' '}
                                    <a
                                        href="/contact-sales"
                                        className="text-purple-600 dark:text-purple-400 hover:underline"
                                    >
                                        Contact Support
                                    </a>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminSubscriptionLoginPage;
